/** One factory record inside a `SupplierFactoryGroup.factories` array. */
export interface ReferenceFactory {
  _id?: string | { toString(): string };
  factoryName?: string;
}

/** One supplier's factory list from the BOM library master, as held in `supplierFactories`. */
export interface SupplierFactoryGroup {
  supplierId?: string;
  factories?: ReferenceFactory[];
}

/**
 * Resolves a set of in-use factory ids to display names, for the BM-9761 "factory in use"
 * instruction message.
 *
 * `supplierFactories` groups factories under the supplier that owns them, so it is flattened
 * first — an in-use factory must resolve to a name even when its owning supplier is no longer
 * selected on the product (the whole reason it needs its own lock: see `factoryLock.ts`). Falls
 * back to the raw id so a factory missing from the reference list is never silently dropped from
 * the message.
 * @param inUseFactoryIds Factory ids currently locked on the product.
 * @param supplierFactories The full, unfiltered supplier -> factories reference list.
 * @returns A comma-separated list of display names (or raw ids where no name is found).
 */
export const resolveInUseFactoryNames = (inUseFactoryIds: Iterable<string>, supplierFactories: SupplierFactoryGroup[] | null | undefined): string => {
  const allFactories = (supplierFactories || []).flatMap((group) => (Array.isArray(group?.factories) ? group.factories : []));
  const nameById = new Map<string, string>(
    allFactories
      .filter((factory): factory is ReferenceFactory & { _id: string | { toString(): string } } => factory?._id !== null && factory?._id !== undefined)
      .map((factory) => [String(factory._id), factory.factoryName || String(factory._id)]),
  );
  return Array.from(inUseFactoryIds)
    .map((factoryId) => nameById.get(factoryId) || factoryId)
    .join(', ');
};
